import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';

/** Mirrors BE `features.productivity.login.dto.ProductivityCode`. */
export interface ProductivityCode {
  id: string;
  description: string;
}

/** Mirrors BE `ProductivityLoginContext`. */
export interface ProductivityLoginContext {
  enabled: boolean;
  warehouseId: string | null;
  departmentId: string | null;
  shiftId: string | null;
  jobId: string | null;
  actSeqNum: number | null;
  complete: boolean;
  moverPresent: boolean;
}

/** Mirrors BE `ProductivityLoginOptions.UsrWarehouseDefaults` — `dbxx.usr_warehouse`
 *  `default_department_id` / `default_shift_id` / `default_job_id`, any of which may be null. */
export interface UsrWarehouseDefaults {
  departmentId: string | null;
  shiftId: string | null;
  jobId: string | null;
}

/** Mirrors BE `ProductivityLoginOptions`. */
export interface ProductivityLoginOptions {
  warehouses: ProductivityCode[];
  departments: ProductivityCode[];
  shifts: ProductivityCode[];
  jobs: ProductivityCode[];
  /** What PB's `warehouse_id` itemchanged stamps over department / shift / job
   *  (`uo_pop_misc.sru:1261-1276`). */
  defaults: UsrWarehouseDefaults | null;
}

/** Mirrors BE `ProductivityLoginRequest`. */
export interface ProductivityLoginPayload {
  warehouseId: string;
  departmentId: string;
  shiftId: string;
  jobId: string;
}

/** Mirrors BE `ProductivityLoginResult`. */
export interface ProductivityLoginResult extends ProductivityLoginPayload {
  actSeqNum: number | null;
  stoppedActSeqNum: number | null;
  clockStarted: boolean;
}

export const productivityLoginKeys = {
  context: ['productivity', 'login-context'] as const,
  options: (warehouseId: string | null) => ['productivity', 'options', warehouseId] as const,
};

/**
 * The dialog seed — PB's change variant reads the live `dbxx.mover` row
 * (`uo_pop_misc.sru:1177-1180`). ADR-0039 part B.
 *
 * `enabled` here is the TanStack gate, not PB's setting: the profile menu only asks for the
 * context while the menu is open, so an operator who never opens the menu costs zero requests.
 * The response's own `enabled` field is PB's `EnableProductivityTrackCRT`.
 *
 * `staleTime: 0` on purpose — the whole point of the dialog is to change these four values, so a
 * cached copy would show the operator what they had before their own save.
 */
export function useProductivityLoginContext(queryEnabled = true) {
  return useQuery({
    queryKey: productivityLoginKeys.context,
    queryFn: () => api<ProductivityLoginContext>('/api/productivity/login-context'),
    enabled: queryEnabled,
    staleTime: 0,
  });
}

/**
 * The four dropdown lists, re-fetched per warehouse — PB re-`setsqlselect`s the department /
 * shift / job children against the picked warehouse (`uo_pop_misc.sru:1248-1257`).
 *
 * Passing `null` asks the server for the session's current warehouse, so the first open needs no
 * extra round trip to discover it.
 */
export function useProductivityLoginOptions(warehouseId: string | null, queryEnabled = true) {
  return useQuery({
    queryKey: productivityLoginKeys.options(warehouseId),
    queryFn: () =>
      api<ProductivityLoginOptions>(
        warehouseId
          ? `/api/productivity/options?warehouseId=${encodeURIComponent(warehouseId)}`
          : '/api/productivity/options',
      ),
    enabled: queryEnabled,
    staleTime: 60 * 1000,
  });
}

/**
 * The save — `UPDATE dbxx.mover` then stop-then-start the clock, one transaction
 * (`uo_pop_misc.sru:1287-1292`, `m_main.srm:3469-3505`).
 *
 * Refusals arrive as 422 problem+json whose `detail` is PB's verbatim message, so the caller
 * shows `error.message` rather than inventing its own wording.
 */
export function useProductivityLoginMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProductivityLoginPayload) =>
      api<ProductivityLoginResult>('/api/productivity/login', { method: 'POST', json: payload }),
    onSuccess: () => {
      // PB caches the committed values in globals (m_main.srm:3515-3519); our equivalent is the
      // context query, which the profile menu reads for its inline summary.
      void qc.invalidateQueries({ queryKey: productivityLoginKeys.context });
    },
  });
}
